import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {ListRange} from 'react-virtuoso';

import {apolloClientNoCache} from '../../apolloClient';
import {useRunLogLinesQuery} from '../../generated/graphql';
import {ApolloError} from '../../state/graphql/apolloWithAbort';
import {propagateErrorsContext} from '../../util/errors';
import {
  type LogLines,
  PAGE_SIZE_IMPROVED_PAGINATION_MAX,
  usePolling,
  useRetryInitialLoad,
  type UseRunLogQueryParams,
} from './runLogQueryCommon';

/**
 * Fetch a little before the visible window so fast scrolls are less likely to
 * land on blank rows while the requested page is still loading.
 */
const GLOBAL_LOG_RANGE_FETCH_CUSHION = 500;

/**
 * Wait briefly before prefetching neighboring pages, so active scrolling does
 * not enqueue stale lookahead requests for every tiny range change.
 */
export const GLOBAL_LOG_RANGE_FETCH_DEBOUNCE_MS = 50;

/**
 * Keep large range fetches serial; the latest visible range should win over old
 * prefetches instead of competing with several 2k-line responses at once.
 */
const GLOBAL_LOG_MAX_CONCURRENT_RANGE_FETCHES = 1;

/**
 * Query timeout for range fetches
 */
export const GLOBAL_LOG_RANGE_FETCH_TIMEOUT_MS = 30_000;

/** Preserve extra loaded rows around the viewport when trimming the sparse cache. */
const GLOBAL_LOG_CACHE_LINE_CUSHION = PAGE_SIZE_IMPROVED_PAGINATION_MAX * 2;

/** Bound heap growth while still keeping the viewport cushion and recent tail. */
const GLOBAL_LOG_MAX_CACHED_LINES = PAGE_SIZE_IMPROVED_PAGINATION_MAX * 8;

/**
 * Keep only a few queued ranges so stale prefetches do not pile up behind the
 * visible range the user actually needs.
 */
const GLOBAL_LOG_MAX_PENDING_RANGE_FETCHES = 3;

/**
 * Keep the most recent pages even when the user is viewing an older range, so
 * polling can merge new lines against the known tail and jump-to-current can
 * render immediately without refetching the end of the log.
 */
const GLOBAL_LOG_TAIL_CACHE_LINE_COUNT = PAGE_SIZE_IMPROVED_PAGINATION_MAX * 2;

// backend pagination query uses cursors that are just encoded line numbers
// knowing this, we can query by line numbers using the same decode/encode functions
export function encodeRunLogCursor(line: number) {
  return btoa(JSON.stringify({line}));
}

export function decodeRunLogCursor(cursor: string) {
  return JSON.parse(atob(cursor));
}

export type GlobalLogLine = {
  id: string;
  label?: string | null;
  level?: string | null;
  line: string;
  lineNum: number;
  number: number;
  timestamp: string;
};

export interface UseRunLogGlobalQueryResult {
  error?: ApolloError;
  globalLinesByNumber: Map<number, GlobalLogLine>;
  initialTopMostItemIndex?: number;
  isLoadingRange: boolean;
  isPolling?: boolean;
  isRetryingRangeFetch: boolean;
  lineCount: number;
  lines: GlobalLogLine[];
  loading: boolean;
  newLineCountWhileDetached: number;
  rangeFetchError?: Error | string;
  retryRangeFetch: () => void;
  retryInitialLoad?: () => void;
  setIsAtCurrent: (isAtCurrent: boolean) => void;
  tailLine?: number;
  totalCount?: number;
  fetchRangeForVisibleRange: (range: ListRange) => void;
  jumpToTop: () => void;
  jumpToCurrent: () => void;
}

export function useRunLogGlobalQuery({
  entityName,
  pollInterval,
  projectName,
  runName,
  skip = false,
}: UseRunLogQueryParams): UseRunLogGlobalQueryResult {
  const pageSize = PAGE_SIZE_IMPROVED_PAGINATION_MAX;
  const {data, loading, error, fetchMore, refetch} = useRunLogLinesQuery({
    client: apolloClientNoCache,
    context: propagateErrorsContext(),
    fetchPolicy: 'no-cache',
    pollInterval: undefined,
    skip,
    variables: {
      entityName,
      projectName,
      runName,
      last: pageSize,
      useImprovedPagination: true,
    },
  });

  const [isRetrying, setIsRetrying] = useState(false);
  const [linesByNumber, setLinesByNumber] = useState(
    () => new Map<number, GlobalLogLine>()
  );
  const linesByNumberRef = useRef(linesByNumber);
  const [isLoadingRange, setIsLoadingRange] = useState(false);
  // highest known line number in the cache
  const [tailLine, setTailLine] = useState<number | undefined>();
  const [initialTopMostItemIndex, setInitialTopMostItemIndex] = useState<
    number | undefined
  >();
  const [newLineCountWhileDetached, setNewLineCountWhileDetached] = useState(0);
  const [lineNumberError, setLineNumberError] = useState<
    ApolloError | undefined
  >();
  const [rangeFetchError, setRangeFetchError] = useState<Error | string>();
  const [isRetryingRangeFetch, setIsRetryingRangeFetch] = useState(false);

  const state = data?.project?.run?.state;

  // used to guard against merging stale responses from a previous run
  const currentRunPath = `${entityName}/${projectName}/${runName}`;
  const currentRunPathRef = useRef(currentRunPath);
  const previousRunPathRef = useRef(currentRunPath);
  // oxlint-disable-next-line react/refs
  currentRunPathRef.current = currentRunPath;

  // Mutable coordination state for range fetching, polling, and scroll position.
  // These refs avoid rerendering for bookkeeping that only async callbacks need.
  const isAtCurrentRef = useRef(true);
  const visibleRangeRef = useRef<ListRange | null>(null);
  const loadedRangesRef = useRef<LineRange[]>([]);
  const inFlightRangesRef = useRef<LineRange[]>([]);
  const activeFetchCountRef = useRef(0);
  const activeRangeFetchCountRef = useRef(0);
  const pendingRangesRef = useRef<PendingRangeFetch[]>([]);
  const rangeFetchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const tailLineRef = useRef<number | undefined>(undefined);
  const initialTopMostItemIndexRef = useRef<number | undefined>(undefined);
  const hasSeenVisibleRangeRef = useRef(false);
  const lastFailedVisibleRangeRef = useRef<FetchGlobalRangeArgs | null>(null);
  const fetchRangeRef = useRef<
    ((args: FetchGlobalRangeArgs) => Promise<void>) | null
  >(null);

  useEffect(() => {
    return () => {
      currentRunPathRef.current = '';
      fetchRangeRef.current = null;
      pendingRangesRef.current = [];
      lastFailedVisibleRangeRef.current = null;
      if (rangeFetchTimeoutRef.current) {
        clearTimeout(rangeFetchTimeoutRef.current);
      }
    };
  }, []);

  // clean up when detecting a run switch
  useEffect(() => {
    if (previousRunPathRef.current === currentRunPath) {
      return;
    }

    previousRunPathRef.current = currentRunPath;
    visibleRangeRef.current = null;
    loadedRangesRef.current = [];
    inFlightRangesRef.current = [];
    activeFetchCountRef.current = 0;
    activeRangeFetchCountRef.current = 0;
    pendingRangesRef.current = [];
    lastFailedVisibleRangeRef.current = null;
    hasSeenVisibleRangeRef.current = false;
    tailLineRef.current = undefined;
    initialTopMostItemIndexRef.current = undefined;
    if (rangeFetchTimeoutRef.current) {
      clearTimeout(rangeFetchTimeoutRef.current);
      rangeFetchTimeoutRef.current = undefined;
    }
    isAtCurrentRef.current = true;
    const emptyLinesByNumber = new Map<number, GlobalLogLine>();
    linesByNumberRef.current = emptyLinesByNumber;
    setLinesByNumber(emptyLinesByNumber);
    setIsLoadingRange(false);
    setTailLine(undefined);
    setInitialTopMostItemIndex(undefined);
    setNewLineCountWhileDetached(0);
    setLineNumberError(undefined);
    setRangeFetchError(undefined);
    setIsRetryingRangeFetch(false);
  }, [currentRunPath]);

  const isBlocked = skip || loading || isRetrying;
  const [retryInitialLoad, retryError] = useRetryInitialLoad({
    isBlocked: isBlocked || isLoadingRange,
    refetch,
    setIsRetrying,
  });

  const mergeEdges = useCallback(
    (
      edges: readonly {node: RunLogLineNode}[] | undefined,
      source: 'initial' | 'range' | 'poll'
    ) => {
      if (!edges) {
        return;
      }
      if (edges.length === 0) {
        setLineNumberError(undefined);
        return;
      }

      const lines = edges.map(edgeToGlobalLogLine);
      if (lines.some(line => line == null)) {
        setLineNumberError(createGlobalLogLineNumberError());
        return;
      }
      setLineNumberError(undefined);

      const globalLines = lines as GlobalLogLine[];
      const lineNumbers = globalLines.map(line => line.number);
      const previousTailLine = tailLineRef.current;
      const nextTailLine = Math.max(previousTailLine ?? -1, ...lineNumbers);
      const nextLinesByNumber = new Map(linesByNumberRef.current);
      for (const line of globalLines) {
        nextLinesByNumber.set(line.number, line);
      }
      const prunedLinesByNumber = pruneGlobalLogLineCache({
        linesByNumber: nextLinesByNumber,
        tailLine: nextTailLine,
        visibleRange: visibleRangeRef.current,
      });
      linesByNumberRef.current = prunedLinesByNumber;
      loadedRangesRef.current = numbersToRanges(
        Array.from(prunedLinesByNumber.keys())
      );
      tailLineRef.current = nextTailLine;
      setLinesByNumber(prunedLinesByNumber);
      setTailLine(nextTailLine);

      if (source === 'initial' && initialTopMostItemIndexRef.current == null) {
        initialTopMostItemIndexRef.current = nextTailLine;
        setInitialTopMostItemIndex(nextTailLine);
      }

      if (source === 'poll') {
        const addedCount =
          previousTailLine == null
            ? 0
            : lineNumbers.filter(number => number > previousTailLine).length;
        if (isAtCurrentRef.current) {
          setNewLineCountWhileDetached(0);
        } else if (addedCount > 0) {
          setNewLineCountWhileDetached(count => count + addedCount);
        }
      }
    },
    []
  );

  const scheduleNextPendingRangeFetch = useCallback(() => {
    setTimeout(() => {
      if (
        fetchRangeRef.current == null ||
        activeRangeFetchCountRef.current >=
          GLOBAL_LOG_MAX_CONCURRENT_RANGE_FETCHES ||
        pendingRangesRef.current.length === 0
      ) {
        return;
      }

      const [pendingRange, ...rest] = pendingRangesRef.current;
      pendingRangesRef.current = rest;
      fetchRangeRef.current?.({
        source: 'range',
        start: pendingRange.start,
        priority: pendingRange.priority,
      });
    }, 0);
  }, []);

  useEffect(() => {
    mergeEdges(data?.project?.run?.logLines?.edges, 'initial');
  }, [data?.project?.run?.logLines?.edges, mergeEdges]);

  const fetchRange = useCallback(
    async ({priority = 'visible', source, start}: FetchGlobalRangeArgs) => {
      const knownTailLine = tailLineRef.current;
      if (
        source === 'range' &&
        knownTailLine != null &&
        start > knownTailLine
      ) {
        return;
      }

      const end =
        source === 'range' && knownTailLine != null
          ? Math.min(start + pageSize - 1, knownTailLine)
          : start + pageSize - 1;
      const requestRange = {start, end};
      const isRequestInFlight = isRangeCovered(
        requestRange,
        inFlightRangesRef.current
      );
      const isRequestLoaded =
        source === 'range' &&
        isRangeCovered(requestRange, loadedRangesRef.current);
      const shouldSkipRange = isRequestInFlight || isRequestLoaded;
      if (skip) {
        return;
      }
      if (shouldSkipRange) {
        if (isRequestLoaded && source === 'range' && priority === 'visible') {
          lastFailedVisibleRangeRef.current = null;
          setRangeFetchError(undefined);
        }
        if (source === 'range') {
          scheduleNextPendingRangeFetch();
        }
        return;
      }

      if (
        source === 'range' &&
        activeRangeFetchCountRef.current >=
          GLOBAL_LOG_MAX_CONCURRENT_RANGE_FETCHES
      ) {
        pendingRangesRef.current = queuePendingRangeFetch(
          pendingRangesRef.current,
          {priority, start}
        );
        return;
      }

      activeFetchCountRef.current += 1;
      if (source === 'range') {
        activeRangeFetchCountRef.current += 1;
        setIsLoadingRange(true);
      }
      inFlightRangesRef.current = mergeLineRanges(inFlightRangesRef.current, [
        requestRange,
      ]);
      const variables = {
        after: start <= 0 ? undefined : encodeRunLogCursor(start - 1),
        before: undefined,
        entityName,
        first: pageSize,
        last: undefined,
        projectName,
        runName,
        useImprovedPagination: true,
      };
      let shouldApplyRangeState = true;

      try {
        const result = await rejectAfterTimeout(
          fetchMore({
            variables,
            // Returning null skips Apollo's cache write. mergeEdges handles the
            // network result below, so the cache intentionally stays as-is.
            updateQuery: () => null as never,
          }),
          GLOBAL_LOG_RANGE_FETCH_TIMEOUT_MS
        );

        if (currentRunPathRef.current !== currentRunPath) {
          shouldApplyRangeState = false;
          return;
        }

        const edges = result.data?.project?.run?.logLines?.edges;
        mergeEdges(edges, source);
        if (source === 'range' && priority === 'visible') {
          lastFailedVisibleRangeRef.current = null;
          setRangeFetchError(undefined);
        }
      } catch (rangeError) {
        if (currentRunPathRef.current !== currentRunPath) {
          shouldApplyRangeState = false;
          return;
        }

        if (source === 'range' && priority === 'visible') {
          pendingRangesRef.current = [];
          lastFailedVisibleRangeRef.current = {source, start, priority};
          setRangeFetchError(normalizeRangeFetchError(rangeError));
        }
      } finally {
        if (
          shouldApplyRangeState &&
          currentRunPathRef.current === currentRunPath
        ) {
          if (source === 'range') {
            activeRangeFetchCountRef.current = Math.max(
              0,
              activeRangeFetchCountRef.current - 1
            );
          }
          activeFetchCountRef.current = Math.max(
            0,
            activeFetchCountRef.current - 1
          );
          inFlightRangesRef.current = subtractLineRange(
            inFlightRangesRef.current,
            requestRange
          );
          setIsLoadingRange(activeRangeFetchCountRef.current > 0);
          if (source === 'range') {
            scheduleNextPendingRangeFetch();
          }
        }
      }
    },
    [
      currentRunPath,
      entityName,
      fetchMore,
      mergeEdges,
      pageSize,
      projectName,
      runName,
      scheduleNextPendingRangeFetch,
      skip,
    ]
  );
  // oxlint-disable-next-line react/refs
  fetchRangeRef.current = fetchRange;

  const retryRangeFetch = useCallback(() => {
    const failedRange = lastFailedVisibleRangeRef.current;
    if (failedRange == null || isRetryingRangeFetch || skip) {
      return;
    }

    setIsRetryingRangeFetch(true);
    void fetchRange(failedRange).finally(() => {
      if (currentRunPathRef.current === currentRunPath) {
        setIsRetryingRangeFetch(false);
      }
    });
  }, [currentRunPath, fetchRange, isRetryingRangeFetch, skip]);

  const fetchRangeForVisibleRange = useCallback(
    (range: ListRange) => {
      visibleRangeRef.current = range;
      const isFirstVisibleRange = !hasSeenVisibleRangeRef.current;
      hasSeenVisibleRangeRef.current = true;
      const start = Math.max(
        0,
        getGlobalLogPageStartForIndex(
          range.startIndex - GLOBAL_LOG_RANGE_FETCH_CUSHION,
          pageSize
        )
      );
      void fetchRange({source: 'range', start, priority: 'visible'});

      if (isFirstVisibleRange) {
        return;
      }

      if (rangeFetchTimeoutRef.current) {
        clearTimeout(rangeFetchTimeoutRef.current);
      }
      rangeFetchTimeoutRef.current = setTimeout(() => {
        rangeFetchTimeoutRef.current = undefined;
        const visibleRange = visibleRangeRef.current;
        if (!visibleRange) {
          return;
        }
        const visibleStart = Math.max(
          0,
          getGlobalLogPageStartForIndex(
            visibleRange.startIndex - GLOBAL_LOG_RANGE_FETCH_CUSHION,
            pageSize
          )
        );
        const previousStart = visibleStart - pageSize;
        if (previousStart >= 0) {
          void fetchRange({
            source: 'range',
            start: previousStart,
            priority: 'prefetch',
          });
        }

        const nextStart = visibleStart + pageSize;
        const knownTailLine = tailLineRef.current;
        if (knownTailLine == null || nextStart <= knownTailLine) {
          void fetchRange({
            source: 'range',
            start: nextStart,
            priority: 'prefetch',
          });
        }
      }, GLOBAL_LOG_RANGE_FETCH_DEBOUNCE_MS);
    },
    [fetchRange, pageSize]
  );

  const jumpToTop = useCallback(() => {
    visibleRangeRef.current = {
      startIndex: 0,
      endIndex: Math.min(pageSize - 1, tailLineRef.current ?? pageSize - 1),
    };
    void fetchRange({source: 'range', start: 0, priority: 'visible'});
  }, [fetchRange, pageSize]);

  const jumpToCurrent = useCallback(() => {
    setNewLineCountWhileDetached(0);
    if (tailLine != null) {
      const start = Math.max(0, tailLine - pageSize + 1);
      const pageStart = getGlobalLogPageStartForIndex(start, pageSize);
      visibleRangeRef.current = {
        startIndex: pageStart,
        endIndex: tailLine,
      };
      void fetchRange({
        source: 'range',
        start: pageStart,
        priority: 'visible',
      });
    }
  }, [fetchRange, pageSize, tailLine]);

  const setIsAtCurrent = useCallback((isAtCurrent: boolean) => {
    isAtCurrentRef.current = isAtCurrent;
    if (isAtCurrent) {
      setNewLineCountWhileDetached(0);
    }
  }, []);

  const canPoll = state === 'running' && pollInterval > 0;
  const pollFetchNext = useCallback(() => {
    if (tailLine == null) {
      void refetch();
      return;
    }
    void fetchRange({source: 'poll', start: tailLine + 1});
  }, [fetchRange, refetch, tailLine]);

  const {isPolling} = usePolling({
    canPoll,
    fetchNext: pollFetchNext,
    hasNextPage: false,
    pollInterval,
  });

  const lines = useMemo(
    () => Array.from(linesByNumber.values()).sort(compareGlobalLogLines),
    [linesByNumber]
  );
  const hasInitialEdges =
    (data?.project?.run?.logLines?.edges?.length ?? 0) > 0;
  const isWaitingForInitialMerge =
    hasInitialEdges && tailLine == null && lineNumberError == null;
  // Apollo v3 also reports failed fetchMore requests on the parent observable
  // query. Once the initial logs are available, keep the viewer mounted and
  // surface those failures through rangeFetchError instead.
  const initialLoadError = linesByNumber.size === 0 ? error : undefined;

  return {
    error: retryError || lineNumberError || initialLoadError,
    fetchRangeForVisibleRange,
    globalLinesByNumber: linesByNumber,
    initialTopMostItemIndex,
    isLoadingRange,
    isPolling,
    isRetryingRangeFetch,
    jumpToCurrent,
    jumpToTop,
    lineCount: lines.length,
    lines,
    loading: isRetrying || loading || isWaitingForInitialMerge,
    newLineCountWhileDetached,
    rangeFetchError,
    retryRangeFetch,
    retryInitialLoad,
    setIsAtCurrent,
    tailLine,
    totalCount: tailLine == null ? undefined : tailLine + 1,
  };
}

type RunLogLineNode = LogLines['edges'][number]['node'] & {
  number?: number | null;
};

type LineRange = {
  start: number;
  end: number;
};

type FetchGlobalRangeArgs = {
  priority?: 'visible' | 'prefetch';
  source: 'range' | 'poll';
  start: number;
};

type PendingRangeFetch = Required<Pick<FetchGlobalRangeArgs, 'priority'>> &
  Pick<FetchGlobalRangeArgs, 'start'>;

function queuePendingRangeFetch(
  current: PendingRangeFetch[],
  next: PendingRangeFetch
) {
  const withoutDuplicate = current.filter(range => range.start !== next.start);
  if (next.priority === 'visible') {
    return [
      next,
      ...withoutDuplicate.filter(range => range.priority !== 'visible'),
    ].slice(0, GLOBAL_LOG_MAX_PENDING_RANGE_FETCHES);
  }

  const visibleRanges = withoutDuplicate.filter(
    range => range.priority === 'visible'
  );
  const prefetchLimit =
    GLOBAL_LOG_MAX_PENDING_RANGE_FETCHES - visibleRanges.length;
  if (prefetchLimit <= 0) {
    return visibleRanges.slice(0, GLOBAL_LOG_MAX_PENDING_RANGE_FETCHES);
  }

  const prefetchRanges = [
    ...withoutDuplicate.filter(range => range.priority !== 'visible'),
    next,
  ].slice(-prefetchLimit);

  return [...visibleRanges, ...prefetchRanges];
}

function edgeToGlobalLogLine({
  node,
}: {
  node: RunLogLineNode;
}): GlobalLogLine | null {
  const number = node.number;
  if (number == null || !Number.isFinite(number)) {
    return null;
  }

  return {
    id: node.id ?? `${number}`,
    label: node.label,
    level: node.level,
    line: node.line ?? '',
    lineNum: number + 1,
    number,
    timestamp: node.timestamp ?? '',
  };
}

function createGlobalLogLineNumberError() {
  return new ApolloError({
    errorMessage:
      'Log line is missing a stable number required for global pagination.',
  });
}

function normalizeRangeFetchError(error: unknown): Error | string {
  if (error instanceof Error || typeof error === 'string') {
    return error;
  }

  return new ApolloError({
    errorMessage: 'Failed to fetch log lines.',
  });
}

function compareGlobalLogLines(a: GlobalLogLine, b: GlobalLogLine) {
  return a.number - b.number;
}

function getGlobalLogPageStartForIndex(index: number, pageSize: number) {
  return Math.max(0, Math.floor(index / pageSize) * pageSize);
}

function numbersToRanges(numbers: number[]): LineRange[] {
  const sorted = [...new Set(numbers)].sort((a, b) => a - b);
  if (!sorted.length) {
    return [];
  }

  const ranges: LineRange[] = [];
  let start = sorted[0];
  let end = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    const number = sorted[i];
    if (number === end + 1) {
      end = number;
    } else {
      ranges.push({start, end});
      start = number;
      end = number;
    }
  }
  ranges.push({start, end});
  return ranges;
}

function mergeLineRanges(current: LineRange[], next: LineRange[]) {
  const sorted = [...current, ...next].sort((a, b) => a.start - b.start);
  const ranges: LineRange[] = [];

  for (const range of sorted) {
    const previous = ranges[ranges.length - 1];
    if (!previous || range.start > previous.end + 1) {
      ranges.push({...range});
    } else {
      previous.end = Math.max(previous.end, range.end);
    }
  }

  return ranges;
}

function pruneGlobalLogLineCache({
  linesByNumber,
  tailLine,
  visibleRange,
}: {
  linesByNumber: Map<number, GlobalLogLine>;
  tailLine?: number;
  visibleRange?: ListRange | null;
}) {
  if (linesByNumber.size <= GLOBAL_LOG_MAX_CACHED_LINES) {
    return linesByNumber;
  }

  const rangesToKeep: LineRange[] = [];
  if (visibleRange) {
    rangesToKeep.push({
      start: Math.max(
        0,
        visibleRange.startIndex - GLOBAL_LOG_CACHE_LINE_CUSHION
      ),
      end: visibleRange.endIndex + GLOBAL_LOG_CACHE_LINE_CUSHION,
    });
  }
  if (tailLine != null) {
    rangesToKeep.push({
      start: Math.max(0, tailLine - GLOBAL_LOG_TAIL_CACHE_LINE_COUNT + 1),
      end: tailLine,
    });
  }
  if (!rangesToKeep.length) {
    return linesByNumber;
  }

  const pruned = new Map<number, GlobalLogLine>();
  for (const [number, line] of linesByNumber) {
    if (isLineNumberInRanges(number, rangesToKeep)) {
      pruned.set(number, line);
    }
  }

  return pruned.size > 0 ? pruned : linesByNumber;
}

function isLineNumberInRanges(number: number, ranges: LineRange[]) {
  return ranges.some(range => number >= range.start && number <= range.end);
}

function isRangeCovered(range: LineRange, ranges: LineRange[]) {
  return ranges.some(
    existing => existing.start <= range.start && existing.end >= range.end
  );
}

function subtractLineRange(ranges: LineRange[], rangeToRemove: LineRange) {
  const next: LineRange[] = [];
  for (const range of ranges) {
    if (range.end < rangeToRemove.start || range.start > rangeToRemove.end) {
      next.push(range);
      continue;
    }

    if (range.start < rangeToRemove.start) {
      next.push({start: range.start, end: rangeToRemove.start - 1});
    }
    if (range.end > rangeToRemove.end) {
      next.push({start: rangeToRemove.end + 1, end: range.end});
    }
  }
  return next;
}

function rejectAfterTimeout<T>(promise: Promise<T>, timeoutMs: number) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(
        new ApolloError({
          errorMessage: 'Timed out loading log lines.',
        })
      );
    }, timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId != null) {
      clearTimeout(timeoutId);
    }
  });
}

export const __runLogGlobalQueryTestUtils = {
  getGlobalLogPageStartForIndex,
  isRangeCovered,
  mergeLineRanges,
  numbersToRanges,
  pruneGlobalLogLineCache,
  queuePendingRangeFetch,
  subtractLineRange,
};
